import { type Face, ROOM_FACES, type RoomShape } from '../model/faces'
import type { MaterialId } from '../model/materials'
import type { ShapeInput, V3 } from './geometry'
import { ccw, edgeCrossings, nearestBoundary, type Pt, perimeter, pointInContour, signedArea } from './polygon'

/** The room as the acoustics and the walking code see it. Everything is centred on x/z = 0 with the floor at y = 0. */
export interface RoomInput {
  shape?: RoomShape
  /** Width x, height y, depth z. Round and ring rooms are circular: x = z = diameter (an ellipse if they differ). */
  size: V3
  /** Core column diameter (ring only). */
  core?: number
  /** Floor outline (polygon only), centred on the origin. */
  outline?: Pt[]
  materials: Partial<Record<Face, MaterialId>>
}

export const shapeOf = (room: RoomInput): RoomShape => room.shape ?? 'box'

export const materialOf = (room: RoomInput, face: Face): MaterialId => room.materials[face] ?? 'concrete'

/** Radius of the core column (0 when the room has none). */
export function coreRadius(room: RoomInput): number {
  if (shapeOf(room) !== 'ring') return 0
  const [w, , d] = room.size
  return Math.min((room.core ?? 0) / 2, 0.9 * Math.min(w, d) * 0.5)
}

/** The ring room's core as a solid cylinder, so it blocks and reflects like any other primitive. */
export function coreShape(room: RoomInput): ShapeInput | null {
  const rc = coreRadius(room)
  if (rc <= 0) return null
  return {
    id: 'room:core',
    name: 'Core column',
    kind: 'cylinder',
    position: [0, room.size[1] / 2, 0],
    rotation: [0, 0, 0],
    size: [rc * 2, room.size[1], rc * 2],
    material: materialOf(room, 'core'),
  }
}

/** The curved outer wall of a round or ring room, as a cylinder shape (used by the reflection solver). */
export function wallShape(room: RoomInput): ShapeInput {
  return {
    id: 'room:wall',
    name: 'Wall',
    kind: 'cylinder',
    position: [0, room.size[1] / 2, 0],
    rotation: [0, 0, 0],
    size: [room.size[0], room.size[1], room.size[2]],
    material: materialOf(room, 'wall'),
  }
}

/** The polygon outline wound counter-clockwise, so the room interior is on the left of every edge. */
export function outlineOf(room: RoomInput): Pt[] {
  return ccw(room.outline ?? [])
}

export function insideRoom(room: RoomInput, p: V3, eps = 1e-6): boolean {
  const [w, h, d] = room.size
  if (p[1] < -eps || p[1] > h + eps) return false
  if (shapeOf(room) === 'polygon') return pointInContour(outlineOf(room), p[0], p[2])
  if (shapeOf(room) === 'box') return Math.abs(p[0]) <= w / 2 + eps && Math.abs(p[2]) <= d / 2 + eps
  const k = Math.hypot(p[0] / (w / 2), p[2] / (d / 2))
  if (k > 1 + eps) return false
  return Math.hypot(p[0], p[2]) >= coreRadius(room) - eps
}

/** Pull a point inside the room, at least `margin` metres away from every surface. */
export function clampToRoom(room: RoomInput, p: V3, margin: number): V3 {
  const [w, h, d] = room.size
  const y = Math.min(h - margin, Math.max(margin, p[1]))
  if (shapeOf(room) === 'polygon') {
    const c = outlineOf(room)
    let x = p[0]
    let z = p[2]
    // Pull inside, then keep at least margin from every wall; a few passes settle tight corners.
    for (let pass = 0; pass < 4; pass++) {
      const inside = pointInContour(c, x, z)
      const near = nearestBoundary([c], x, z)
      if (inside && near.d >= margin - 1e-9) break
      const a = c[near.edge]!
      const b = c[(near.edge + 1) % c.length]!
      const l = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1
      const nx = -(b[1] - a[1]) / l // inward (left) normal of a counter-clockwise outline
      const nz = (b[0] - a[0]) / l
      x = near.q[0] + nx * margin
      z = near.q[1] + nz * margin
    }
    return [x, y, z]
  }
  if (shapeOf(room) === 'box') {
    return [Math.min(w / 2 - margin, Math.max(-w / 2 + margin, p[0])), y, Math.min(d / 2 - margin, Math.max(-d / 2 + margin, p[2]))]
  }
  let x = p[0]
  let z = p[2]
  const rc = coreRadius(room)
  const limit = 1 - margin / Math.min(w / 2, d / 2)
  // Two passes: pushing out of the core can push towards the outer wall, and vice versa.
  for (let pass = 0; pass < 2; pass++) {
    const k = Math.hypot(x / (w / 2), z / (d / 2))
    if (k > limit) {
      x *= limit / k
      z *= limit / k
    }
    if (rc > 0) {
      const r = Math.hypot(x, z)
      if (r < rc + margin) {
        const ux = r > 1e-9 ? x / r : 1
        const uz = r > 1e-9 ? z / r : 0
        x = ux * (rc + margin)
        z = uz * (rc + margin)
      }
    }
  }
  return [x, y, z]
}

// ---- Surfaces and volume -------------------------------------------------------------------------

/** Ramanujan's approximation for the perimeter of an ellipse with semi-axes a, b. */
export function ellipsePerimeter(a: number, b: number): number {
  const h = ((a - b) / (a + b)) ** 2
  return Math.PI * (a + b) * (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h)))
}

export function roomVolume(room: RoomInput): number {
  const [w, h, d] = room.size
  if (shapeOf(room) === 'polygon') return Math.abs(signedArea(room.outline ?? [])) * h
  if (shapeOf(room) === 'box') return w * h * d
  const rc = coreRadius(room)
  return (Math.PI * (w / 2) * (d / 2) - Math.PI * rc * rc) * h
}

/** Area of every surface the room has, keyed by face. */
export function roomSurfaces(room: RoomInput): { face: Face; area: number }[] {
  const [w, h, d] = room.size
  const shape = shapeOf(room)
  if (shape === 'polygon') {
    const c = room.outline ?? []
    const floor = Math.abs(signedArea(c))
    return ROOM_FACES.polygon.map((face) => ({ face, area: face === 'wall' ? perimeter(c) * h : floor }))
  }
  if (shape === 'box') {
    return ROOM_FACES.box.map((face) => ({
      face,
      area: face === 'floor' || face === 'ceiling' ? w * d : face === 'west' || face === 'east' ? d * h : w * h,
    }))
  }
  const rc = coreRadius(room)
  const disc = Math.PI * (w / 2) * (d / 2) - Math.PI * rc * rc
  return ROOM_FACES[shape].map((face) => ({
    face,
    area: face === 'floor' || face === 'ceiling' ? disc : face === 'wall' ? ellipsePerimeter(w / 2, d / 2) * h : 2 * Math.PI * rc * h,
  }))
}

/** How many of a polygon room's walls the straight path a→b crosses (0 for every other shape). */
export function wallCrossings(room: RoomInput, a: V3, b: V3): number {
  if (shapeOf(room) !== 'polygon') return 0
  return edgeCrossings(room.outline ?? [], a[0], a[2], b[0], b[2])
}
